import React, {
  type ChangeEvent,
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from 'react'

import type { ReactInputProps } from '../../types'
import { SCREENREADER_ONLY_STYLES } from '../../util/commonStyles'
import { CssVars } from '../themes'
import { InternalCheckboxOrRadioButtonRow } from './internal/InternalCheckboxOrRadioButtonRow'
import { useUniqueId } from './useUniqueId'

export const testIds = {
  visibleRadio: 'rh-RadioButton-visibleRadio',
}

const RADIO_BUTTON_PIXEL_SIZE = 16

export function RadioButton({
  _experimentalVariant = 'default',
  label,
  ...radioButtonProps
}: {
  label?: string
  _experimentalVariant?: 'default' | 'legacy'
} & ComponentProps<typeof StandaloneRadioButton>) {
  if (label) {
    return (
      <InternalCheckboxOrRadioButtonRow
        _experimentalVariant={_experimentalVariant}
        input={<StandaloneRadioButton {...radioButtonProps} />}
        inputHeight={RADIO_BUTTON_PIXEL_SIZE}
        label={label}
      />
    )
  }
  return <StandaloneRadioButton {...radioButtonProps} />
}

type BaseProps = {
  state?: 'default' | 'disabled' | false
  value: string
  id?: string
}
type InputProps = Omit<
  ReactInputProps,
  | keyof BaseProps
  | 'ref'
  | 'className'
  | 'style'
  | 'css'
  | 'type'
  | 'disabled'
  | 'checked'
>
type StandaloneRadioButtonProps = BaseProps & InputProps
export function StandaloneRadioButton({
  id,
  state = 'default',
  value,
  ...inputProps
}: StandaloneRadioButtonProps) {
  const { name, onChange, value: selectedValue } = useRadioButtonGroupContext()
  const isChecked = selectedValue === value
  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <div css={{ display: 'inline-block' }}>
      <input
        {...inputProps}
        ref={inputRef}
        checked={isChecked}
        // TODO(eric.lovett): Remove this class name once legacy input styles are removed
        // from web-app.
        className="remove-legacy"
        // NOTE(jare): We render this radio button to get a bunch of built in browser
        // behaviors for radio inputs (e.g. keyboard navigation, mouse events on labels),
        // then use CSS to style a sibling element to look like the design system radio
        // button
        css={SCREENREADER_ONLY_STYLES}
        disabled={state === 'disabled'}
        id={id}
        name={name}
        onChange={onChange}
        type="radio"
        value={value}
      />
      <div
        // NOTE(jare): We hide this div from screen readers since it's only meant to
        // visually indicate the status of the radio button
        aria-hidden="true"
        css={[
          {
            backgroundColor: CssVars.neutral.bg1,
            borderColor: CssVars.neutral.fg2,
            borderRadius: '50%',
            borderStyle: 'solid',
            borderWidth: 1,
            boxSizing: 'border-box',
            height: RADIO_BUTTON_PIXEL_SIZE,
            width: RADIO_BUTTON_PIXEL_SIZE,
          },

          {
            // NOTE(jare): Ideally we'd use focus-visible here, since that appears
            // to be the behavior for radio buttons across Chrome, Safari, and FF.
            // Currently the web-app applies the .no-focus-outline class to the
            // body if the user is using a pointing device to focus elements, so
            // we respect that classname here.
            // TODO(jare): Update this if we use decide to use :focus-visible or
            // switch to a polyfill when we can
            'body:not(.no-focus-outline) input:focus + &': {
              boxShadow: `
                    0 0 0 1px ${CssVars.focus.innerColor},
                    0 0 0 4px ${CssVars.focus.outerColor};
                `,
            },
          },

          {
            'input:active + &, &:active': {
              borderColor: CssVars.neutral.fg3,
            },
            'input:disabled + &': {
              backgroundColor: CssVars.neutral.bg2,
              borderColor: CssVars.neutral.bg3,
            },
            'input:hover + &, &:hover': {
              borderColor: CssVars.neutral.fg1,
            },
          },
          isChecked && {
            borderColor: CssVars.semantic.primary.base,
            borderWidth: 5,

            'input:active + &, &:active': {
              borderColor: CssVars.semantic.primary.pressed,
            },
            'input:disabled + &': {
              borderColor: CssVars.neutral.fg3,
            },
            'input:hover + &, &:hover': {
              borderColor: CssVars.semantic.primary.hover,
            },
          },
        ]}
        data-testid={testIds.visibleRadio}
        onClick={e => {
          // NOTE(jare): When this component is not the child of a label element, then
          // clicks on this div won't automatically be propagated to the input, so we
          // need to trigger them ourselves. If this component **is** rendered inside
          // of a label element, then clicks on this div will propagate up to the label
          // and trigger a click event on the input. We call preventDefault here to skip
          // over any native result of clicking this div and make sure we register a
          // single click on the input.
          e.preventDefault()

          // NOTE(jare): Forward any clicks on the presentational piece
          // of this component to the actual radio input and focus it after
          inputRef.current?.click()
          inputRef.current?.focus()
        }}
      />
    </div>
  )
}

type RadioButtonGroupContextValue = {
  name: string
  value: string | null
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}
const RadioButtonGroupContext =
  createContext<RadioButtonGroupContextValue | null>(null)
export function RadioButtonGroup({
  children,
  onChange,
  value,
}: {
  children: ReactNode
  value: string | null
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}) {
  const name = useUniqueId()
  const contextValue = useMemo(
    () => ({ name, onChange, value }),
    [name, onChange, value]
  )
  return (
    <RadioButtonGroupContext.Provider value={contextValue}>
      {children}
    </RadioButtonGroupContext.Provider>
  )
}

export function useRadioButtonGroupContext(): RadioButtonGroupContextValue {
  const value = useContext(RadioButtonGroupContext)
  if (!value) {
    throw new Error(
      'This component must be rendered inside of a <RadioButtonGroup>'
    )
  }
  return value
}
